import React, {
  createContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { Box, CircularProgress } from '@mui/material';
import { StorageKeys, AuthEndpoints, AuthPaths } from './AuthConfig';

// User info from the token response
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  roles: string[];
}

// Token response from the server
interface TokenResponse {
  accessToken: string;
  expiresIn: number;
  tokenType: string;
  user: AuthUser;
}

// Auth context value
export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  accessToken: string | null;
  login: (returnUrl?: string) => void;
  logout: () => Promise<void>;
  /**
   * Refreshes the access token by exchanging the cookie session for a new JWT.
   * This does NOT use a refresh token - it uses the XX-day (based on configuration) sliding cookie session.
   */
  refreshAccessToken: () => Promise<boolean>;
}

// Create context with default values
export const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  accessToken: null,
  login: () => {},
  logout: async () => {},
  refreshAccessToken: async () => false,
});

interface Props {
  children: ReactNode;
}

/**
 * Auth Provider that handles JWT authentication with cookie-based login.
 *
 * Authentication flow:
 * 1. Check if user has valid cookie session (from Razor Pages login)
 * 2. If yes, exchange cookie for JWT token
 * 3. Store JWT in localStorage for API calls
 * 4. On token expiry, try to refresh via cookie exchange
 * 5. If cookie expired, redirect to Razor Pages login
 */
export const AuthProvider: React.FC<Props> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Save auth state to localStorage
  const saveAuthState = useCallback(
    (token: string, expiresIn: number, userData: AuthUser) => {
      const expiry = Date.now() + expiresIn * 1000;
      localStorage.setItem(StorageKeys.AccessToken, token);
      localStorage.setItem(StorageKeys.TokenExpiry, expiry.toString());
      localStorage.setItem(StorageKeys.User, JSON.stringify(userData));
      setAccessToken(token);
      setUser(userData);
    },
    []
  );

  // Clear auth state
  const clearAuthState = useCallback(() => {
    localStorage.removeItem(StorageKeys.AccessToken);
    localStorage.removeItem(StorageKeys.TokenExpiry);
    localStorage.removeItem(StorageKeys.User);
    setAccessToken(null);
    setUser(null);
  }, []);

  // Check if stored token is still valid
  const isTokenValid = useCallback((): boolean => {
    const expiry = localStorage.getItem(StorageKeys.TokenExpiry);
    if (!expiry) return false;
    return Date.now() < parseInt(expiry, 10);
  }, []);

  // Exchange cookie session for JWT token
  const exchangeCookieForToken = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch(AuthEndpoints.Token, {
        method: 'GET',
        credentials: 'include', // Include cookies
      });

      if (!response.ok) {
        console.log('Cookie exchange failed:', response.status);
        return false;
      }

      const data: TokenResponse = await response.json();
      saveAuthState(data.accessToken, data.expiresIn, data.user);
      console.log('Token obtained via cookie exchange for:', data.user.email);
      return true;
    } catch (error) {
      console.error('Error exchanging cookie for token:', error);
      return false;
    }
  }, [saveAuthState]);

  // Refresh access token by exchanging cookie for new JWT (no refresh token used)
  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    return exchangeCookieForToken();
  }, [exchangeCookieForToken]);

  // Redirect to Razor Pages login
  const login = useCallback((returnUrl?: string) => {
    const url = returnUrl || window.location.pathname + window.location.search;
    window.location.href = `${AuthPaths.Login}?returnUrl=${encodeURIComponent(url)}`;
  }, []);

  // Logout - call API to clear cookie, then clear localStorage
  const logout = useCallback(async () => {
    try {
      // First clear local state
      clearAuthState();

      // Then call the API to sign out (clears the cookie)
      await fetch(AuthEndpoints.Logout, {
        method: 'POST',
        credentials: 'include',
      });

      console.log('Logged out successfully');
    } catch (error) {
      console.error('Error during logout:', error);
    }

    // Redirect to home page
    window.location.href = AuthPaths.Home;
  }, [clearAuthState]);

  // Initialize auth on mount
  useEffect(() => {
    const initAuth = async () => {
      // First check localStorage for existing token
      const storedToken = localStorage.getItem(StorageKeys.AccessToken);
      const storedUser = localStorage.getItem(StorageKeys.User);

      if (storedToken && storedUser && isTokenValid()) {
        // Use stored token if valid
        setAccessToken(storedToken);
        setUser(JSON.parse(storedUser));
        setIsLoading(false);
        console.log('Using stored token');
        return;
      }

      // Try to exchange cookie for token
      const success = await exchangeCookieForToken();
      if (!success) {
        // No valid cookie session - clear any stale data
        clearAuthState();
      }
      setIsLoading(false);
    };

    initAuth();
  }, [isTokenValid, exchangeCookieForToken, clearAuthState]);

  const contextValue: AuthContextValue = {
    isAuthenticated: !!accessToken && !!user,
    isLoading,
    user,
    accessToken,
    login,
    logout,
    refreshAccessToken,
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
